// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Activity, Database, Radio, Server } from 'lucide-react';

import { connectivityLabels } from '../../config/marketplaceContent';
import { useTheme } from '../../context/ThemeContext';
import { apiBaseUrl } from '../../util/api';

import css from './ConnectivityTicker.module.css';

type ServiceStatus = 'online' | 'degraded' | 'offline';

type ServiceState = {
  id: string;
  label: string;
  status: ServiceStatus;
};

const defaultServices: ServiceState[] = [
  { id: 'api', label: connectivityLabels.api, status: 'offline' },
  { id: 'hyperlane', label: connectivityLabels.hyperlane, status: 'degraded' },
  { id: 'anvil', label: connectivityLabels.anvil, status: 'degraded' },
  { id: 'postgres', label: connectivityLabels.postgres, status: 'degraded' },
];

const iconFor = (id: string) => {
  switch (id) {
    case 'api':
      return Server;
    case 'hyperlane':
      return Radio;
    case 'anvil':
      return Activity;
    default:
      return Database;
  }
};

const ConnectivityTicker: React.FC = () => {
  const { isDark } = useTheme();
  const [services, setServices] = useState<ServiceState[]>(defaultServices);

  useEffect(() => {
    let cancelled = false;

    const probe = async () => {
      const next = [...defaultServices];

      try {
        const base = apiBaseUrl(process.env.REACT_APP_MARKETPLACE_ROOT_URL);
        const res = await fetch(`${base}/api/noticeboard/feed`, { credentials: 'include' });
        const apiIdx = next.findIndex(s => s.id === 'api');
        if (apiIdx >= 0) {
          next[apiIdx] = { ...next[apiIdx], status: res.ok ? 'online' : 'degraded' };
        }
      } catch (_) {
        const apiIdx = next.findIndex(s => s.id === 'api');
        if (apiIdx >= 0) {
          next[apiIdx] = { ...next[apiIdx], status: 'offline' };
        }
      }

      const hyperlaneIdx = next.findIndex(s => s.id === 'hyperlane');
      if (hyperlaneIdx >= 0) {
        next[hyperlaneIdx] = {
          ...next[hyperlaneIdx],
          status: typeof window !== 'undefined' && window.__E2E_CHAIN_ID__ ? 'online' : 'degraded',
        };
      }

      const anvilIdx = next.findIndex(s => s.id === 'anvil');
      if (anvilIdx >= 0) {
        next[anvilIdx] = {
          ...next[anvilIdx],
          status: typeof window !== 'undefined' && window.ethereum ? 'online' : 'degraded',
        };
      }

      const pgIdx = next.findIndex(s => s.id === 'postgres');
      if (pgIdx >= 0) {
        next[pgIdx] = { ...next[pgIdx], status: next[0]?.status === 'online' ? 'online' : 'degraded' };
      }

      if (!cancelled) {
        setServices(next);
      }
    };

    probe();
    const intervalId = window.setInterval(probe, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div
      className={isDark ? css.root : css.rootLight}
      data-testid="connectivity-ticker"
      aria-label="Infrastructure connectivity"
    >
      {services.map(service => {
        const Icon = iconFor(service.id);
        return (
          <span key={service.id} className={css.item} data-status={service.status}>
            <Icon size={12} aria-hidden="true" />
            <span className={css.label}>{service.label}</span>
            <span className={css.dot} />
          </span>
        );
      })}
    </div>
  );
};

export default ConnectivityTicker;
